import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LearningOpportunity, LearningOpportunityCreate } from '~/shared/data-contract/learning-opportunities';
import { learningOpportunitySchema } from '~/shared/data-contract/learning-opportunities';

import { invalidateLearningOpportunityCaches } from './cache-invalidation';
import { learningOpportunityPathnames } from './query-keys';

export type LearningOpportunityMutationResult = UseMutationResult<
    LearningOpportunity,
    Error,
    LearningOpportunityCreate
>;

export const useCreateNewLearningOpportunityMutation = <T extends 'postJson'>(
    operationName: T,
): LearningOpportunityMutationResult => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (learningOpportunity) => {
            const result = await validatingCobrainerApiFetcher[operationName](learningOpportunitySchema, {
                pathname: learningOpportunityPathnames.pathname,
                payload: learningOpportunity,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        mutationKey: [learningOpportunityPathnames.pathname, operationName],
        onSuccess: () => {
            invalidateLearningOpportunityCaches(queryClient);
        },
    });
};
